import { MEET_NO_MAX } from "@seagames/contracts";
import { z } from "zod";
import type { MeetStatusFilters } from "../../lib/meet/queries";
import { STATUS_GROUPS, type StatusGroup, groupFilter } from "../dashboard/status-groups";
import { dayRange, isDay } from "../home/day";

/**
 * `GET /meets` (and `/unscheduled`, `/status-counts`, `/days`) filters plus
 * R3b's `medal_events` — A3's `MeetStatusFilters` predates it, and
 * `filtersToQuery` passes the extra key through as `medal_events=true`.
 */
export type MeetsListFilters = MeetStatusFilters & { medal_events?: boolean };

/**
 * `?medal=true` in a page URL: only the meets that decide medals. A boolean,
 * so the router writes it as `true` (a string "1" would be JSON-quoted).
 */
export const medalParam = z.literal(true).optional().catch(undefined);

/**
 * `?no=401` in a page URL: the meet number, exactly (`meet_no` on the API).
 * The router reads `401` as a number and writes a string JSON-quoted, so both
 * are accepted and kept as text ("007" stays "007").
 */
export const meetNoParam = z
	.union([z.string(), z.number()])
	.transform(String)
	.pipe(z.string().trim().min(1).max(MEET_NO_MAX))
	.optional()
	.catch(undefined);

/** A typed meet number → the URL's: trimmed, empty = no filter, cut to the API's bound. */
export const meetNoFromDraft = (v: string): string | undefined => v.trim().slice(0, MEET_NO_MAX) || undefined;

/**
 * Meets › All sports: the filters live in the URL (`/meets?sport=…&day=…&status=live`)
 * so Back, a reload and a copied link all show the same list. A bad value is
 * dropped, never an error page. The games is the sidebar's current games.
 */
const id = z.string().uuid().optional().catch(undefined);
export const meetsSearch = z.object({
	sport: id,
	/** Only meaningful with `sport` — it names one of that sport's disciplines. */
	discipline: id,
	venue: id,
	/** `YYYY-MM-DD` in the games' zone; absent = every day. */
	day: z.string().refine(isDay).optional().catch(undefined),
	status: z.enum(STATUS_GROUPS).optional().catch(undefined),
	/** The meet number, exactly — in every event the other filters keep. */
	no: meetNoParam,
	/** Medal events only (a phase that awards medals, of a medal event). */
	medal: medalParam,
});
export type MeetsSearch = z.infer<typeof meetsSearch>;

/** The FilterBar's draft: empty strings for "any", like a form. */
export interface MeetsDraft extends Record<string, unknown> {
	sport: string;
	discipline: string;
	venue: string;
	day: string;
	status: StatusGroup | "";
	no: string;
	medal: boolean;
}

export const EMPTY_DRAFT: MeetsDraft = { sport: "", discipline: "", venue: "", day: "", status: "", no: "", medal: false };

export const draftFromSearch = (s: MeetsSearch): MeetsDraft => ({
	sport: s.sport ?? "",
	discipline: s.sport ? (s.discipline ?? "") : "",
	venue: s.venue ?? "",
	day: s.day ?? "",
	status: s.status ?? "",
	no: s.no ?? "",
	medal: s.medal === true,
});

/** Draft → URL: empties dropped, a discipline only together with its sport. */
export function searchFromDraft(d: MeetsDraft): MeetsSearch {
	return {
		sport: d.sport || undefined,
		discipline: d.sport && d.discipline ? d.discipline : undefined,
		venue: d.venue || undefined,
		day: d.day && isDay(d.day) ? d.day : undefined,
		status: d.status || undefined,
		no: meetNoFromDraft(d.no),
		medal: d.medal ? true : undefined,
	};
}

/** How many filters are set (the Reset badge). */
export const activeFilterCount = (s: MeetsSearch): number =>
	[s.sport, s.sport ? s.discipline : undefined, s.venue, s.day, s.status, s.no, s.medal].filter((v) => v !== undefined).length;

/** URL → the `GET /meets` filters, for one games. A status group becomes its two status lists. */
export function meetsFilters(gamesId: string, s: MeetsSearch): MeetsListFilters {
	const status = s.status ? groupFilter(s.status) : undefined;
	return {
		games_id: gamesId,
		sport_id: s.sport,
		discipline_id: s.sport ? s.discipline : undefined,
		venue_id: s.venue,
		...(s.day ? dayRange(s.day) : {}),
		...(status ? { schedule_status: status.schedule_status, result_status: status.result_status } : {}),
		...(s.no ? { meet_no: s.no } : {}),
		...(s.medal ? { medal_events: true } : {}),
	};
}
